import { styled } from "styled-components";
import { Link } from "@ludens-reklame/rubics-v3-sdk/link/types";
import { Image } from "@ludens-reklame/rubics-v3-sdk/file/types";
import { mobileBreakPointCSS } from "../../defaults/theme.js";
import { Constrainer } from "../../internal-components/Layout.js";
import { cssFontSize, cssSpacing } from "../../util/styles.js";
import { useLinkProps } from "../../hooks/useLinkProps.js";
import { HorizontalSpacer } from "../../internal-components/Layout.js";
import { BackgroundColor, MobileColumns } from "../../types/theme.js";
import { useImageProps } from "../../hooks/useImageProps.js";
import { RichText } from "../../internal-components/RichText.js";
import { Card as BaseCard, CardBody } from "../../internal-components/Card.js";

export type RelatedArticlesColumns = 2 | 3 | 4;

export interface RelatedArticlesProps {
  title?: string;
  list?: Partial<ListItem>[];
  columns?: RelatedArticlesColumns;
  mobileColumns?: MobileColumns;
  backgroundColor?: BackgroundColor;
}

interface ListItem {
  link: Link;
  text?: string;
  image?: Image;
}

export const RelatedArticles: React.FC<RelatedArticlesProps> = ({
  title,
  list = [],
  columns = 3,
  mobileColumns = 1,
  backgroundColor,
}) => {
  return (
    <RelatedArticlesVerticalSpacer>
      <HorizontalSpacer>
        <RelatedArticlesConstrainer>
          {title && <h2>{title}</h2>}
          <RelatedArticlesGrid
            $columns={columns}
            $mobileColumns={mobileColumns}
          >
            {list
              .filter((x) => x.link)
              .map((item, index) => (
                <Item key={index} {...(item as ListItem)} $bg={backgroundColor} />
              ))}
          </RelatedArticlesGrid>
        </RelatedArticlesConstrainer>
      </HorizontalSpacer>
    </RelatedArticlesVerticalSpacer>
  );
};

const Item: React.FC<ListItem & { $bg?: BackgroundColor }> = ({ text = "", link, image, $bg }) => {
  const linkProps = useLinkProps(link);

  const imageProps = useImageProps(
    image || link?.image,
    {
      width: 716,
      height: 400,
      fit: "cover",
    },
    "lazy"
  );

  const _text = text || link.text;

  return (
    <Card {...linkProps} $bg={$bg}>
      {imageProps && <img {...imageProps} />}
      <CardContent>
        <h4>{link.title}</h4>
        {text ? (
          <p>{_text}</p>
        ) : link.text ? (
          <RichText dangerouslySetInnerHTML={{ __html: link.text }} />
        ) : null}
      </CardContent>
    </Card>
  );
};

export const RelatedArticlesVerticalSpacer = styled.div``;

export const RelatedArticlesConstrainer = styled(Constrainer)`
  padding: ${cssSpacing("l", 0, "l", 0)};

  h2 {
    text-align: center;
    margin-bottom: ${cssSpacing("l")};
    margin-top: 0;
    color: var(--related-articles-title-color, inherit);
  }

  @media (min-width: ${mobileBreakPointCSS}) {
    padding: ${cssSpacing("xl")};
  }
`;

export const RelatedArticlesGrid = styled.div<{
  $columns: RelatedArticlesColumns;
  $mobileColumns: MobileColumns;
}>`
  display: grid;
  grid-template-columns: repeat(${(props) => props.$mobileColumns}, minmax(0, 1fr));
  gap: ${cssSpacing("l", "m")};

  @media (min-width: ${mobileBreakPointCSS}) {
    grid-template-columns: repeat(${(props) => props.$columns || 3}, minmax(0, 1fr));
  }
`;

export const CardContent = styled(CardBody)`
  h4 {
    margin: 0 0 ${(props) => props.theme.spacing.xs};
    color: var(--related-articles-card-title-color, inherit);
    font-size: ${cssFontSize("medium")};
    font-weight: 600;
  }

  p,
  .rich-text {
    margin: 0;
    color: var(--related-articles-card-description-color, inherit);
    font-size: ${cssFontSize("small")};
  }

  p {
    white-space: pre-wrap;
  }
`;

export const Card = styled(BaseCard).attrs({
  $background: "var(--related-articles-card-background)",
})`
  transition: all 0.2s ease-in-out;

  &:hover h4 {
    color: var(--related-articles-card-title-hover-color, var(--swatch1));
  }

  img {
    object-fit: cover;
    width: 100%;
    height: 220px;
    display: block;
  }
`;
